<template>
  <div class="third-party-service">
    <div class="container-fluid py-4">
      <h2 class="h4 fw-medium mb-4">{{ $t("__thirdPartyServiceManagement") }}</h2>
      <div
        class="d-flex align-items-center justify-content-between mb-3 flex-wrap"
      >
        <div class="me-auto d-flex flex-wrap align-items-center">
          <select
            v-model="platformFilter"
            class="form-select rounded-pill me-2 mb-2"
            style="width: auto"
          >
            <option :value="null">{{ $t("__tpsAllPlatforms") }}</option>
            <option v-for="p in platforms" :key="p.value" :value="p.value">
              {{ p.label }}
            </option>
          </select>
          <form @submit.prevent="searchService" class="mb-2">
            <div class="input-group rounded-pill">
              <input
                v-model="search"
                type="text"
                class="form-control rounded-pill"
                :placeholder="$t('__searchAgencyName')"
                aria-label="search"
                aria-describedby="search"
              />
              <button
                type="submit"
                class="btn btn-link px-2 text-dark"
                id="search"
              >
                <i class="bi bi-search"></i>
              </button>
              <button
                v-if="search"
                type="button"
                class="btn btn-primary px-3 ms-1 rounded-pill"
                id="clearSearch"
                @click="clearSearch"
              >
                {{ $t("__clearSearch") }}
              </button>
            </div>
          </form>
        </div>
        <button
          v-if="hasPermission('third-party-services.create')"
          @click="showModal()"
          class="mb-2 fs-5 btn btn-primary px-2 rounded-circle lh-sm"
        >
          <i class="bi bi-plus"></i>
        </button>
      </div>
      <div class="card">
        <div v-if="items" class="card-body">
          <b-table
            :responsive="$store.state.windowWidth > $store.state.breakpoints.sm"
            :stacked="$store.state.windowWidth <= $store.state.breakpoints.sm"
            striped
            borderless
            no-provider-filtering
            head-variant="light"
            :items="items"
            :fields="fields"
            :current-page="currentPage"
            :per-page="perPage"
          >
            <template v-slot:head()="scope">
              <div class="text-nowrap">
                {{ scope.label }}
              </div>
            </template>
            <template v-slot:cell(service_name)="data">
              {{ platformLabel(data.item.service_name) }}
            </template>
            <template v-slot:cell(agency_name)="data">
              {{ data.item.agency_name || '-' }}
            </template>
            <template v-slot:cell(endpoint_url)="data">
              {{ data.item.endpoint_url || '-' }}
            </template>
            <template v-slot:cell(has_api_key)="data">
              <span v-if="data.item.has_api_key" class="badge bg-success">{{ $t("__set") }}</span>
              <span v-else class="badge bg-secondary">{{ $t("__notSet") }}</span>
            </template>
            <template v-slot:cell(is_active)="data">
              <div
                class="status-switch"
                v-if="hasPermission('third-party-services.update')"
              >
                <input
                  type="checkbox"
                  :id="'switch-' + data.index"
                  v-model="data.item.is_active"
                  @click="toggleAlert(data.item.id)"
                />
                <label :for="'switch-' + data.index"></label>
              </div>
              <span v-else>
                {{ data.item.is_active ? $t("__enabled") : $t("__disabled") }}
              </span>
            </template>
            <template v-slot:cell(active)="data">
              <div class="d-flex">
                <button
                  v-if="hasPermission('third-party-services.update')"
                  @click="showModal(data.item.id)"
                  type="button"
                  class="btn btn-link px-1 text-dark"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__edit')"
                >
                  <i class="bi bi-pencil mx-1 fs-5"></i>
                </button>
                <button
                  v-if="hasPermission('third-party-services.delete') && !data.item.is_active"
                  type="button"
                  class="btn btn-link px-1 text-danger"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="$t('__delete')"
                  @click="deleteAlert(data.item.id)"
                >
                  <i class="bi bi-trash fs-5"></i>
                </button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row align-items-center mt-3" v-if="totalRows">
        <div class="col-sm col-12 text-right text-sm-left">
          {{
            $t("__paginationInfo", {
              startNub: currentPage == 1 ? 1 : perPage * (currentPage - 1) + 1,
              entNub:
                currentPage == 1
                  ? totalRows > perPage
                    ? perPage
                    : totalRows
                  : perPage * (currentPage - 1) + perPage > totalRows
                  ? totalRows
                  : perPage * (currentPage - 1) + perPage,
              sum: totalRows,
            })
          }}
        </div>
        <div class="col-sm col-12">
          <b-pagination
            v-if="totalRows > perPage"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="right"
            first-number
            last-number
            class="mb-sm-0 mb-3 mt-sm-0 mt-3"
          ></b-pagination>
        </div>
      </div>
    </div>

    <!-- 編輯/新增 Modal -->
    <div class="modal fade cms-style" tabindex="-1" id="editThirdPartyService" ref="editModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <form @submit.prevent="submitForm">
            <div class="modal-header">
              <h5 class="modal-title fw-medium">
                {{ isEdit ? $t("__editThirdPartyService") : $t("__addThirdPartyService") }}
              </h5>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label fw-medium required">{{ $t("__agencyName") }}</label>
                <v-select
                  v-model="modalData.agency_id"
                  :options="allAgencies"
                  label="name"
                  :reduce="agency => agency.id"
                  :placeholder="$t('__openThisSelectMenu')"
                  :disabled="isEdit"
                  class="style-chooser"
                  :class="{ 'is-invalid': validate && validate.msg.agency_id }"
                  :components="{Deselect, OpenIndicator}"
                  :get-option-label="option => `${option.codename} / ${option.name}`"
                >
                </v-select>
                <div class="invalid-feedback" v-if="validate && validate.msg.agency_id">
                  <div v-for="(msg, index) in validate.msg.agency_id" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium required">{{ $t("__tpsPlatform") }}</label>
                <select
                  v-model="modalData.service_name"
                  class="form-select"
                  :class="{ 'is-invalid': validate && validate.msg.service_name }"
                  :disabled="isEdit"
                  @change="onPlatformChange"
                  required
                >
                  <option :value="null" disabled>{{ $t("__openThisSelectMenu") }}</option>
                  <option v-for="p in platforms" :key="p.value" :value="p.value">
                    {{ p.label }}
                  </option>
                </select>
                <div class="invalid-feedback" v-if="validate && validate.msg.service_name">
                  <div v-for="(msg, index) in validate.msg.service_name" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium">{{ $t("__tpsEndpointUrl") }}</label>
                <input
                  v-model="modalData.endpoint_url"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': validate && validate.msg.endpoint_url }"
                  placeholder="https://xxx.example.com"
                />
                <div class="form-text" v-if="selectedPlatform && selectedPlatform.endpoint_url_hint">
                  {{ selectedPlatform.endpoint_url_hint }}
                </div>
                <div class="invalid-feedback" v-if="validate && validate.msg.endpoint_url">
                  <div v-for="(msg, index) in validate.msg.endpoint_url" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium">{{ $t("__tpsApiKey") }}</label>
                <input
                  v-model="modalData.api_key"
                  type="password"
                  class="form-control"
                  :class="{ 'is-invalid': validate && validate.msg.api_key }"
                  :placeholder="isEdit ? $t('__leaveEmptyNoChange') : ''"
                  autocomplete="new-password"
                />
                <div class="form-text" v-if="selectedPlatform && selectedPlatform.api_key_hint">
                  {{ selectedPlatform.api_key_hint }}
                </div>
                <div class="invalid-feedback" v-if="validate && validate.msg.api_key">
                  <div v-for="(msg, index) in validate.msg.api_key" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label fw-medium">{{ $t("__tpsApiSecret") }}</label>
                <input
                  v-model="modalData.api_secret"
                  type="password"
                  class="form-control"
                  :class="{ 'is-invalid': validate && validate.msg.api_secret }"
                  :placeholder="isEdit ? $t('__leaveEmptyNoChange') : ''"
                  autocomplete="new-password"
                />
                <div class="invalid-feedback" v-if="validate && validate.msg.api_secret">
                  <div v-for="(msg, index) in validate.msg.api_secret" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3">
                <div class="d-flex align-items-center justify-content-between">
                  <label class="form-label fw-medium">{{ $t("__tpsAdditionalSettings") }}</label>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary rounded-pill mb-2"
                    :disabled="!selectedPlatform"
                    @click="fillSettingsTemplate"
                  >
                    {{ $t("__tpsFillTemplate") }}
                  </button>
                </div>
                <textarea
                  v-model="modalData.additional_settings_text"
                  class="form-control font-monospace"
                  :class="{ 'is-invalid': jsonError || (validate && validate.msg.additional_settings) }"
                  rows="6"
                  spellcheck="false"
                ></textarea>
                <div class="form-text" v-if="selectedPlatform && selectedPlatform.settings_hint">
                  {{ selectedPlatform.settings_hint }}
                </div>
                <div class="invalid-feedback d-block" v-if="jsonError">
                  {{ $t("__tpsAdditionalSettingsInvalid") }}
                </div>
                <div class="invalid-feedback" v-if="validate && validate.msg.additional_settings">
                  <div v-for="(msg, index) in validate.msg.additional_settings" :key="index">{{ msg }}</div>
                </div>
              </div>
              <div class="mb-3 d-flex align-items-center">
                <div class="status-switch me-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    v-model="modalData.is_active"
                  />
                  <label for="isActive"></label>
                </div>
                <span>{{ $t("__enabled") }}</span>
              </div>
              <div class="alert alert-danger mb-0" v-if="formError">{{ formError }}</div>
            </div>
            <div class="modal-footer justify-content-between">
              <button
                type="button"
                class="btn btn-outline-secondary rounded-pill"
                data-bs-dismiss="modal"
              >
                {{ $t("__cancel") }}
              </button>
              <button type="submit" class="btn btn-primary rounded-pill">
                {{ $t("__save") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- 刪除確認 Modal -->
    <div
      class="modal fade"
      id="deleteThirdPartyServiceModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      ref="deleteModal"
    >
      <div class="modal-dialog modal-sm">
        <div class="modal-content">
          <div class="modal-header justify-content-center">
            <h5 class="modal-title">{{ $t("__deleteThirdPartyService") }}</h5>
          </div>
          <div class="modal-body text-center">
            <p class="mb-0">
              {{ $t("__deleteThirdPartyServiceNote", { name: modalData.displayName }) }}
            </p>
          </div>
          <div class="modal-footer justify-content-between">
            <button
              type="button"
              class="btn btn-outline-secondary rounded-pill"
              data-bs-dismiss="modal"
            >
              {{ $t("__cancel") }}
            </button>
            <button
              @click="deleteService"
              type="button"
              class="btn btn-primary rounded-pill"
            >
              {{ $t("__confirm") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 切換狀態確認 Modal -->
    <div
      class="modal fade"
      id="toggleThirdPartyServiceModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      ref="toggleModal"
    >
      <div class="modal-dialog modal-sm">
        <div class="modal-content">
          <div class="modal-header justify-content-center">
            <h5 class="modal-title">{{ $t("__changeStatus") }}</h5>
          </div>
          <div class="modal-body text-center">
            <p class="mb-0">
              {{ $t("__changeStatusNote", { Agency: modalData.displayName }) }}
            </p>
          </div>
          <div class="modal-footer justify-content-between">
            <button
              type="button"
              class="btn btn-outline-secondary rounded-pill"
              data-bs-dismiss="modal"
            >
              {{ $t("__cancel") }}
            </button>
            <button
              @click="toggleStatus"
              type="button"
              class="btn btn-primary rounded-pill"
            >
              {{ $t("__confirm") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Tooltip, Modal } from "bootstrap";
import i18n from "@/lang/lang.js";
import { thirdPartyService } from "@/http/api/thirdPartyService.js";
import { agency } from "@/http/api/agency.js";
import { mapState, mapGetters } from "vuex";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "ThirdPartyService",
  components: { vSelect },
  data() {
    return {
      Deselect: {
        render: createElement => createElement('span', ''),
      },
      OpenIndicator: {
        render: createElement => createElement('span', [
          createElement('img', {
            attrs: {
              src: "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e"
            },
            style: { width: '16px', height: '16px' }
          })
        ]),
      },
      services: null,
      allAgencies: [],
      platforms: [],
      platformFilter: null,
      search: null,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      bootstrap_modal: null,
      isEdit: false,
      editModalId: null,
      toggleId: null,
      jsonError: false,
      formError: null,
      modalData: {
        agency_id: null,
        service_name: null,
        endpoint_url: null,
        api_key: null,
        api_secret: null,
        additional_settings_text: "",
        is_active: true,
        displayName: null,
      },
      validate: null,
    };
  },
  computed: {
    ...mapState(["token"]),
    ...mapGetters(["hasPermission"]),
    fields() {
      return [
        {
          key: "service_name",
          label: i18n.t("__tpsPlatform"),
          class: "align-middle",
          sortable: true,
        },
        {
          key: "agency_name",
          label: i18n.t("__agencyName"),
          class: "align-middle",
          sortable: true,
        },
        {
          key: "endpoint_url",
          label: i18n.t("__tpsEndpointUrl"),
          class: "align-middle",
          sortable: false,
        },
        {
          key: "has_api_key",
          label: i18n.t("__tpsKeyStatus"),
          class: "align-middle text-center",
          sortable: false,
        },
        {
          key: "is_active",
          label: i18n.t("__status"),
          class: "align-middle text-center",
          sortable: true,
        },
        {
          key: "active",
          label: i18n.t("__active"),
          tdClass: "align-middle",
          class: "align-middle",
          sortable: false,
        },
      ];
    },
    items() {
      if (!this.services) return null;
      let filtered = this.services;
      if (this.platformFilter) {
        filtered = filtered.filter((item) => item.service_name === this.platformFilter);
      }
      if (this.search) {
        const keyword = this.search.toLowerCase();
        filtered = filtered.filter((item) => {
          return (
            (item.agency_name && item.agency_name.toLowerCase().includes(keyword)) ||
            (item.endpoint_url && item.endpoint_url.toLowerCase().includes(keyword))
          );
        });
      }
      return filtered;
    },
    selectedPlatform() {
      if (!this.platforms) return null;
      return this.platforms.find((p) => p.value === this.modalData.service_name) || null;
    },
  },
  watch: {
    items(data) {
      if (data) {
        this.initBootstrapTooltip();
        this.totalRows = data.length;
        this.currentPage = 1;
      }
    },
  },
  created() {
    this.getServices();
    this.getOptions();
    this.getAgencies();
  },
  methods: {
    getServices() {
      thirdPartyService.get({}, this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.services = res.data.data;
          this.totalRows = this.services.length;
        }
      });
    },
    getOptions() {
      thirdPartyService.getOptions(this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.platforms = res.data.data || [];
        }
      });
    },
    getAgencies() {
      agency.get(null, this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.allAgencies = res.data.data;
        }
      });
    },
    platformLabel(value) {
      const p = (this.platforms || []).find((x) => x.value === value);
      return p ? p.label : (value || "-");
    },
    onPlatformChange() {
      const p = this.selectedPlatform;
      const current = (this.modalData.additional_settings_text || "").trim();
      if (p && !current) {
        this.modalData.additional_settings_text = p.settings_template
          ? JSON.stringify(p.settings_template, null, 2)
          : "";
      }
    },
    fillSettingsTemplate() {
      const p = this.selectedPlatform;
      if (!p) return;
      const current = (this.modalData.additional_settings_text || "").trim();
      // 框內已有內容一律先問過，取消就完全不動
      if (current && !confirm(this.$t("__tpsFillTemplateConfirm"))) return;
      this.modalData.additional_settings_text = p.settings_template
        ? JSON.stringify(p.settings_template, null, 2)
        : "";
    },
    showModal(id) {
      this.validate = null;
      this.jsonError = false;
      this.formError = null;
      if (id) {
        this.isEdit = true;
        this.editModalId = id;
        thirdPartyService.show(id, this.token).then((res) => {
          if (res && res.data && res.data.status === "success") {
            const d = res.data.data;
            this.modalData = {
              agency_id: d.agency_id,
              service_name: d.service_name,
              endpoint_url: d.endpoint_url,
              api_key: null,
              api_secret: null,
              additional_settings_text: JSON.stringify(d.additional_settings || {}, null, 2),
              is_active: d.is_active,
              displayName: this.buildDisplayName(d),
            };
            this.bootstrap_modal = new Modal(this.$refs.editModal);
            this.bootstrap_modal.show();
          }
        });
      } else {
        this.isEdit = false;
        this.editModalId = null;
        this.modalData = {
          agency_id: null,
          service_name: null,
          endpoint_url: null,
          api_key: null,
          api_secret: null,
          additional_settings_text: "",
          is_active: true,
          displayName: null,
        };
        this.bootstrap_modal = new Modal(this.$refs.editModal);
        this.bootstrap_modal.show();
      }
    },
    submitForm() {
      let parsedSettings = {};
      const raw = (this.modalData.additional_settings_text || "").trim();
      if (raw) {
        try {
          parsedSettings = JSON.parse(raw);
        } catch (e) {
          this.jsonError = true;
          return;
        }
      }
      this.jsonError = false;
      this.formError = null;

      // 該平台 additional_settings 的必填 key，缺一就擋下不發請求
      // 元素是陣列＝該組擇一即可（服務類以 ?? 同時相容 camelCase 與 snake_case 兩種寫法）
      const hasSettingValue = (key) => {
        const value = parsedSettings ? parsedSettings[key] : undefined;
        if (value === undefined || value === null) return false;
        return typeof value !== "string" || value.trim() !== "";
      };
      const requiredKeys =
        (this.selectedPlatform && this.selectedPlatform.required_settings) || [];
      const missingKeys = requiredKeys
        .filter((entry) =>
          Array.isArray(entry) ? !entry.some(hasSettingValue) : !hasSettingValue(entry)
        )
        // 缺的時候取陣列第一個（主要寫法）當代表顯示
        .map((entry) => (Array.isArray(entry) ? entry[0] : entry));
      if (missingKeys.length) {
        this.formError = this.$t("__tpsSettingsMissingKeys", {
          keys: missingKeys.join(", "),
        });
        return;
      }

      const data = {
        service_name: this.modalData.service_name,
        agency_id: this.modalData.agency_id,
        endpoint_url: this.modalData.endpoint_url,
        additional_settings: parsedSettings,
        is_active: this.modalData.is_active,
      };
      // 留空＝不修改（編輯）／不設定（新增）
      if (this.modalData.api_key) data.api_key = this.modalData.api_key;
      if (this.modalData.api_secret) data.api_secret = this.modalData.api_secret;

      const request = this.isEdit
        ? thirdPartyService.update(this.editModalId, data, this.token)
        : thirdPartyService.create(data, this.token);

      request
        .then((res) => {
          if (res && res.data && res.data.status === "success") {
            this.bootstrap_modal.hide();
            this.getServices();
          } else if (res && res.data && res.data.validate) {
            this.validate = { msg: res.data.validate };
          } else if (res && res.data && res.data.errMsg) {
            this.formError = res.data.errMsg;
          }
        })
        .catch((err) => {
          if (err.response && err.response.data) {
            this.validate = {
              msg: err.response.data.validate || {},
            };
          }
        });
    },
    toggleAlert(id) {
      this.toggleId = id;
      const svc = this.services.find((i) => i.id === id);
      if (svc) {
        this.modalData.displayName = this.buildDisplayName(svc);
      }
      this.bootstrap_modal = new Modal(this.$refs.toggleModal);
      this.bootstrap_modal.show();
    },
    toggleStatus() {
      thirdPartyService.toggle(this.toggleId, this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.bootstrap_modal.hide();
          this.getServices();
        }
      });
    },
    deleteAlert(id) {
      this.editModalId = id;
      const svc = this.services.find((i) => i.id === id);
      if (svc) {
        this.modalData.displayName = this.buildDisplayName(svc);
      }
      this.bootstrap_modal = new Modal(this.$refs.deleteModal);
      this.bootstrap_modal.show();
    },
    deleteService() {
      thirdPartyService.delete(this.editModalId, this.token).then((res) => {
        if (res && res.data && res.data.status === "success") {
          this.bootstrap_modal.hide();
          this.getServices();
        }
      });
    },
    buildDisplayName(svc) {
      const label = this.platformLabel(svc.service_name);
      return svc.agency_name ? `${svc.agency_name} - ${label}` : label;
    },
    searchService() {
      // search 是 reactive，items computed 會自動過濾
    },
    clearSearch() {
      this.search = null;
    },
    initBootstrapTooltip() {
      this.$nextTick(() => {
        const tooltipTriggerList = [].slice.call(
          document.querySelectorAll('[data-bs-toggle="tooltip"]')
        );
        tooltipTriggerList.map(
          (tooltipTriggerEl) => new Tooltip(tooltipTriggerEl)
        );
      });
    },
  },
};
</script>
